import React from 'react';

import { Inject } from '@odinjs/odin';

import { catchRenderException, setVisibility } from '@philips-emr/hpms-components';
import { AutocompleteAPI, ButtonAPI, FieldAPI, FileUploaderAPI, FormAPI, PanelAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnBeforeSubmit, OnCancel, OnClick, OnDynamicContentRender, OnMount } from '@philips-emr/tws-core/lib/client';
import { AbortableSubmitEvent, ClickEvent, FetchEvent, MountEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService } from '@philips-emr/tws-core/lib/services';
import { applicationContext } from '@philips-emr/tws-foundation';

import { FragmentNames, RootRoutes, SolicitarViaAliases } from '../../codegen';
import { Endpoints } from '../../definitions/endpoints';
import { DialogService, FileService, ToastService } from '../../services';
import BaseController from '../base/base-controller';

@Controller({ fragment: FragmentNames.SOLICITAR_VIA })
export default class SolicitarViaController extends BaseController {

  @Inject(SolicitarViaAliases.PANEL_CONSULTA)
  private panelConsulta: PanelAPI;
  @Inject(SolicitarViaAliases.PANEL_SOLICITAR)
  private panelSolicitar: PanelAPI;
  @Inject(SolicitarViaAliases.PANEL_CONFIRMAR)
  private panelConfirmar: PanelAPI;
  @Inject(SolicitarViaAliases.FORM_SOLICITAR)
  private formSolicitar: FormAPI;
  @Inject(SolicitarViaAliases.AUTOCOMPLETE_MOTIVO)
  private autocompleteMotivo: AutocompleteAPI;
  @Inject(SolicitarViaAliases.FIELD_ANEXOS)
  private fieldAnexos: FieldAPI;

  @OnDynamicContentRender(SolicitarViaAliases.CUSTOM_CARD)
  onDynamicContentRender({ data, render }: RenderEvent): void {
    const { ieStatus, dsStatus } = data as { ieStatus: number, dsStatus: string };

    const props: { [key: string]: {} } = {
      1: { icon: StampIcon.CLOCK, color: Color.SAFETY_NEUTRAL },
      2: { icon: StampIcon.CHECK, color: Color.SAFETY_BLUE },
      3: { icon: StampIcon.CHECK, color: Color.SAFETY_BLUE },
      4: { icon: StampIcon.CHECK, color: Color.SAFETY_GREEN },
      5: { icon: StampIcon.CHECK, color: Color.SAFETY_RED },
    };

    render(() => <Stamp {...props[ieStatus]} label={dsStatus} />);
  }

  @OnMount(SolicitarViaAliases.PANEL_LOADER)
  onLoaderMount({ target }: MountEvent): void {
    const api = target as PanelAPI;
    this.getStatusCartao(api);
  }

  @OnMount(SolicitarViaAliases.FILE_UPLOADER)
  onFileuploaderMount = ({ target }: MountEvent): void => FileService.setPredefinedFiles(12, {
    field: this.fieldAnexos,
    fileUploader: target as FileUploaderAPI,
  });

  @OnAfterFetch(SolicitarViaAliases.FETCH_SOLICITAR)
  onSolicitarAfterFetch({ data }: FetchEvent): void {
    if (data) {
      const { motivos } = data as { motivos: [] };
      this.autocompleteMotivo.setData(motivos);
      setTimeout(() => this.formSolicitar.setEditing(true), 15);
    }
  }

  @OnAfterFetch(SolicitarViaAliases.FETCH_DADOS)
  onDadosAfterFetch = (event: FetchEvent): void => setVisibility(event, catchRenderException(() => this.panelConfirmar));

  @OnBeforeSubmit(SolicitarViaAliases.FORM_SOLICITAR)
  onBeforeSubmit({ abort, target }: AbortableSubmitEvent): void {
    const api = target as FormAPI;
    const { nrSeqBeneficiario } = this.context.getBulkData();

    const isSubmittable: Promise<boolean> = HttpService.post<{}>(Endpoints.POST.CARTAO_SOLICITAR, { nrSeqBeneficiario }, { body: api.getRecord() })
      .then(response => {
        if (response.body) {
          ToastService.open(response);
          api.setEditing(false);
          api.setLocked(true);
          this.getStatusCartao(undefined);
        }
        return false;
      })
      .catch(response => {
        ToastService.open(response);
        return true;
      });

    abort(isSubmittable);
  }

  @OnClick(SolicitarViaAliases.BUTTON_CONFIRMAR)
  onConfirmarClick({ target }: ClickEvent): void {
    const api = target as ButtonAPI;
    const { nrSeqBeneficiario } = this.context.getBulkData();

    api.setState({ loading: true });
    void DialogService.confirm('beneficiary.confirmar-carteirinha')
      .then(() => {
        HttpService.post<{}>(Endpoints.POST.CARTAO_CONFIRMAR, { nrSeqBeneficiario })
          .then(response => {
            if (response.body) {
              ToastService.open(response);
              this.panelConfirmar.setVisible(false);
            }
          })
          .catch(response => ToastService.open(response));
      })
      .finally(() => api.setState({ loading: false }));
  }

  private getStatusCartao(panelLoaderAPI?: PanelAPI): void {
    const { nrSeqBeneficiario } = this.context.getBulkData();

    HttpService.get<''>(Endpoints.GET.CARTAO_STATUS, { nrSeqBeneficiario })
      .then(({ body }) => {
        if (body) {
          switch (body) {
            case 'A':
              this.panelSolicitar.setVisible(false);
              return this.panelConsulta.setVisible(true);
            case 'CR':
              this.panelConsulta.setVisible(true);
              this.panelConfirmar.setVisible(true);
              return;
            case 'N':
              return this.panelSolicitar.setVisible(true);
          }
        }
      })
      .catch(response => ToastService.open(response))
      .finally(() => {
        if (panelLoaderAPI) {
          panelLoaderAPI.setVisible(false);
        }
      });
  }

  @OnCancel(SolicitarViaAliases.FORM_SOLICITAR)
  onCancelFormSolic(): void {
    applicationContext.redirect(RootRoutes.ROUTE_LOGIN);
  }
  
}
